import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { acquireAssetObjectUrl, assetFetchKey } from "./assetFetch.js";

/**
 * Renders an image from a gcsAsset pointer (or legacy sourceUrl) via auth fetch.
 * Uses refcounted in-memory cache; optional lazy load via IntersectionObserver.
 */
export default function AuthenticatedAssetImage({
    asset,
    sourceUrl,
    alt = "",
    contentType = "",
    sx = {},
    placeholder = "—",
    lazy = false,
}) {
    const rootRef = useRef(null);
    const [visible, setVisible] = useState(!lazy);
    const [blobUrl, setBlobUrl] = useState("");
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);

    const fetchTarget = asset || sourceUrl;
    const key = assetFetchKey(fetchTarget);
    const typeHint = contentType || asset?.contentType || "";

    useEffect(() => {
        if (!lazy || visible) return undefined;
        const el = rootRef.current;
        if (!el || typeof IntersectionObserver === "undefined") {
            setVisible(true);
            return undefined;
        }
        const io = new IntersectionObserver(
            (entries) => {
                if (entries.some((e) => e.isIntersecting)) {
                    setVisible(true);
                    io.disconnect();
                }
            },
            { rootMargin: "120px", threshold: 0.01 }
        );
        io.observe(el);
        return () => io.disconnect();
    }, [lazy, visible]);

    useEffect(() => {
        if (!visible || !key) {
            setBlobUrl("");
            setFailed(false);
            setLoading(false);
            return undefined;
        }

        let cancelled = false;
        let release = () => {};

        async function load() {
            setLoading(true);
            setFailed(false);
            try {
                const result = await acquireAssetObjectUrl(key, typeHint);
                if (cancelled) {
                    result.release();
                    return;
                }
                release = result.release;
                setBlobUrl(result.blobUrl);
            } catch {
                if (!cancelled) {
                    setFailed(true);
                    setBlobUrl("");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        load();
        return () => {
            cancelled = true;
            release();
        };
    }, [visible, key, typeHint]);

    if (!key) {
        return (
            <Typography component="span" sx={{ color: "#9aa3ad", fontSize: 13 }}>
                {placeholder}
            </Typography>
        );
    }

    return (
        <Box
            ref={rootRef}
            sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: sx.width || undefined,
                minHeight: sx.height || undefined,
            }}
        >
            {!visible || (loading && !blobUrl) ? (
                <CircularProgress size={18} />
            ) : failed || !blobUrl ? (
                <Typography component="span" sx={{ color: "#9aa3ad", fontSize: 13 }}>
                    {placeholder}
                </Typography>
            ) : (
                <Box
                    component="img"
                    src={blobUrl}
                    alt={alt}
                    onError={() => setFailed(true)}
                    sx={{
                        display: "block",
                        objectFit: "contain",
                        ...sx,
                    }}
                />
            )}
        </Box>
    );
}
